import { type Configuration, DirectoryEntityType, type ListMicrosoft365GroupMembersSort, type Snapshot, type SearchDirectoryEntitiesResponse, type Microsoft365User, type Microsoft365SharePointSite, SaasCaptureOutcome, type SaasCaptureReason, type SaasEntity, type SaasEntitySnapshot, GetGwsBackupFilterApi, GetSaasEntityIdByExternalIdApi, type GetSaasEntityIdByExternalIdRequest, ListGoogleWorkspaceGroupsApi, ListGoogleWorkspaceOrgUnitsApi, ListGoogleWorkspaceSharedDrivesApi, ListInventoryItemSnapshotsApi, ListMicrosoft365UsersApi, ListMicrosoft365GroupsApi, ListMicrosoft365GroupMembersApi, GetMicrosoft365BackupScopeApi, type Microsoft365EntityFilters, type Microsoft365PolicyConfig, ListMicrosoft365SharePointSitesApi, SaasEntitiesApi, SaasEntityType, UpdateShouldFullBackupResourceApi, FileExplorerListPathApi, SearchDirectoryEntitiesApi, ListGroupConversationsApi } from '@repo/api-gw-sdk';
import { mutate } from 'swr';
import type { GWGroupNode, GWSharedDriveNode, GWUserNode } from '@/app/(dashboard)/saas-inventory/google-workspace/googleWorkspaceTypes';
import type { GWSnapshot } from '@/app/(dashboard)/saas-inventory/google-workspace/snapshotTypes';
import { dayjs } from '@/utils/dayjs';
import { decodePageIndex, encodePageToken } from '@/utils/string';
import type { EntraIdGlobalSearchResponse } from './entraIdGlobalDirectorySearchTypes';
import type { HttpClient } from './httpClient';
interface DirectoryEntityRef {
  entityId: string;
  entityType: DirectoryEntityType;
}

// A heavily consumed API can pull in hundreds of entities, so batch reads go out
// a few at a time rather than all at once.
const DIRECTORY_BATCH_CONCURRENCY = 6;

/**
 * Reads a batch has already completed, owned by the caller (one per open
 * restore dialog) so that a cached record never outlives the viewer who was
 * allowed to fetch it. A snapshot's index never changes, so when one entity of a
 * batch fails, the retry refetches only what is still missing.
 */
export type DirectoryReadCache = Map<string, Promise<unknown>>;
function cachedDirectoryRead<T>(cache: DirectoryReadCache | undefined, key: string, read: () => Promise<T>) {
  if (!cache) {
    return read();
  }
  const cached = cache.get(key);
  if (cached) {
    return cached as Promise<T>;
  }
  const pending = read();
  cache.set(key, pending);
  pending.catch(() => cache.delete(key));
  return pending;
}
async function mapWithConcurrency<T, R>(items: T[], limit: number, map: (item: T) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await map(items[index]);
    }
  };
  await Promise.all(Array.from({
    length: Math.min(limit, items.length)
  }, worker));
  return results;
}
const backupSettingsUrl = '/inventory/backup-settings';
const orgUnitsUrl = '/saas-inventory/google-workspace/org-units';
const groupsUrl = '/saas-inventory/google-workspace/groups';
const sharedDrivesUrl = '/saas-inventory/google-workspace/shared-drives';
const snapshotsBaseUrl = '/saas-inventory/google-workspace';
const entraIdBaseUrl = '/saas-inventory/entra-id';
const m365UsersUrl = '/saas-inventory/microsoft-365/users';
const m365GroupsUrl = '/saas-inventory/microsoft-365/groups';
const m365GroupMembersUrl = '/saas-inventory/microsoft-365/group-members';
const m365SharePointSitesUrl = '/saas-inventory/microsoft-365/sharepoint-sites';
const m365BackupScopeUrl = '/saas-inventory/microsoft-365/backup-scope';
const m365EntitiesUrl = '/saas-inventory/microsoft-365/entities';
const gwsEntitiesUrl = '/saas-inventory/google-workspace/entities';
const entitySnapshotsUrl = '/saas-inventory/entity-snapshots';
const entitySnapshotDetailUrl = '/saas-inventory/entity-snapshot-detail';
const entityIdByExternalIdUrl = '/saas-inventory/entity-id-by-external-id';

// terminated and hasSnapshot are set only in entities mode: the registry returns
// tombstones and snapshot presence, which the legacy directory endpoints do not.
export type Microsoft365UserListItem = Microsoft365User & {
  terminated?: boolean;
  hasSnapshot?: boolean;
};
// latestCapture* describe the newest snapshot that recorded the site, which can predate the newest one.
export type Microsoft365SharePointSiteListItem = Microsoft365SharePointSite & {
  terminated?: boolean;
  hasSnapshot?: boolean;
  latestCaptureOutcome?: SaasCaptureOutcome;
  latestCaptureReason?: SaasCaptureReason;
  latestCaptureSnapshotTime?: Date | null;
};
// Teams and their channels exist only in the entity registry (no legacy directory endpoint), so
// their list items are shaped here. teamId/channelId are the Graph ids the backup policy stores.
export interface Microsoft365TeamListItem {
  teamId: string;
  displayName?: string;
  description?: string;
  mail?: string;
  visibility?: string;
  terminated?: boolean;
  hasSnapshot?: boolean;
}

// userId/siteId map to externalId (the Graph id), not entity.id: backup-scope coverage,
// threat lookups and the browse-index prefixes all key on the Graph id.
function mapSaasEntityToMicrosoft365User(entity: SaasEntity): Microsoft365UserListItem {
  const user = entity.properties?.microsoft365User;
  return {
    userId: entity.externalId,
    displayName: entity.displayName,
    userPrincipalName: user?.userPrincipalName,
    mail: user?.email,
    accountEnabled: user?.accountEnabled,
    hasMailbox: user?.hasMailbox,
    hasOnedrive: user?.hasOnedrive,
    mailboxType: user?.mailboxType,
    licenseStatus: user?.licenseStatus,
    licenses: user?.licenses,
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot
  };
}
function mapSaasEntityToSharePointSite(entity: SaasEntity): Microsoft365SharePointSiteListItem {
  return {
    siteId: entity.externalId,
    name: entity.displayName,
    webUrl: entity.properties?.sharePointSite?.webUrl,
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot,
    latestCaptureOutcome: entity.latestCaptureOutcome,
    latestCaptureReason: entity.latestCaptureReason,
    latestCaptureSnapshotTime: entity.latestCaptureSnapshotTime
  };
}
function mapSaasEntityToTeam(entity: SaasEntity): Microsoft365TeamListItem {
  return {
    teamId: entity.externalId ?? '',
    displayName: entity.displayName,
    description: entity.properties?.team?.description,
    mail: entity.properties?.team?.mail,
    visibility: entity.properties?.team?.visibility,
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot
  };
}

// A snapshot that skipped the entity or captured none of it is not a backup of it, so the lists
// show no date for it rather than dating a failed attempt.
const uncapturedOutcomes = new Set<SaasCaptureOutcome>([SaasCaptureOutcome.SAAS_CAPTURE_OUTCOME_SKIPPED, SaasCaptureOutcome.SAAS_CAPTURE_OUTCOME_NOT_CAPTURED]);
const gwsLastBackup = (entity: SaasEntity) => entity.latestCaptureSnapshotTime && entity.latestCaptureOutcome && !uncapturedOutcomes.has(entity.latestCaptureOutcome) ? new Date(entity.latestCaptureSnapshotTime).toISOString() : '';

// id mirrors the vault file-tree path so a row click drills into the same prefix the file listing
// uses; externalId is the Google directory id the browse index keys on.
function mapSaasEntityToGwsUser(entity: SaasEntity): GWUserNode {
  const user = entity.properties?.googleWorkspaceUser;
  const email = user?.email ?? '';
  return {
    id: `users/${entity.externalId}`,
    name: entity.displayName || email || entity.externalId || '',
    type: 'user',
    email,
    lastBackup: gwsLastBackup(entity),
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot,
    saasEntityId: entity.id,
    suspended: user?.suspended,
    archived: user?.archived,
    orgUnitPath: user?.orgUnitPath
  };
}
function mapSaasEntityToGwsSharedDrive(entity: SaasEntity): GWSharedDriveNode {
  return {
    id: `shared_drives/${entity.externalId}`,
    name: entity.displayName || entity.externalId || '',
    type: 'shared-drive',
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot,
    saasEntityId: entity.id,
    lastBackup: gwsLastBackup(entity)
  };
}

// The registry returns tombstones (soft-deleted rows), so terminated rides along and the list marks
// them.
function mapSaasEntityToGwsGroup(entity: SaasEntity): GWGroupNode {
  const email = entity.properties?.group?.email ?? '';
  return {
    id: `groups/${entity.externalId}`,
    name: entity.displayName || email || entity.externalId || '',
    type: 'group',
    email,
    terminated: entity.terminated,
    hasSnapshot: entity.hasSnapshot,
    saasEntityId: entity.id,
    lastBackup: gwsLastBackup(entity)
  };
}

// listSaasEntities decodes its pageToken as a base64 `<prefix>|pageIndex`.
function toEntitiesPageToken(pageToken?: string): string | undefined {
  return pageToken ? encodePageToken(Number(pageToken)) : undefined;
}

// entities.data.nextToken is that same base64 `<prefix>|pageIndex` shape — this keeps
// nextPageToken symmetric with pageToken above, safe for a caller to feed straight back in.
function fromEntitiesPageToken(nextToken?: string): string | undefined {
  return nextToken ? String(decodePageIndex(nextToken)) : undefined;
}
const entityRequest = (type: SaasEntityType, search?: string, microsoft365?: Microsoft365EntityFilters) => ({
  filters: {
    type: {
      _in: [type]
    },
    ...(search ? {
      search
    } : {}),
    ...(microsoft365 ? {
      microsoft365
    } : {})
  }
});

// Matches the gateway's maxDirectoryListIds cap (directory_list_params.go) — a saved selection
// larger than one chunk is split across multiple requests instead of failing the whole resolve.
const DIRECTORY_IDS_CHUNK_SIZE = 500;
const chunkIds = (ids: string[]): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += DIRECTORY_IDS_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + DIRECTORY_IDS_CHUNK_SIZE));
  }
  return chunks;
};

// entityId is the Eon id listSaasEntities returns. Callers that only know the provider id + type
// (search deep links, the Microsoft 365 lists) pass those instead and the hook resolves them first.
interface EntitySnapshotsArgs {
  resourceId: string;
  entityType?: SaasEntityType;
  externalId?: string;
  entityId?: string;
  enabled?: boolean;
}
interface DirectorySearchArgs {
  resourceId: string;
  snapshotId?: string;
  query?: string;
  entityType?: DirectoryEntityType;
  entityId?: string;
  /** Entra ID only: drop enterprise applications the backed-up tenant does not own. */
  tenantOwnedOnly?: boolean;
  /**
   * Entra ID only: search every snapshot taken in this period, returning each
   * entity as the newest one saw it. Sent with snapshotId rather than instead
   * of it — a tenant whose reads still come from the index blob cannot answer
   * across snapshots and serves the named one.
   */
  fromTime?: Date;
  toTime?: Date;
  pageIndex: number;
  pageSize: number;
}
function snapshotToGWSnapshot(snapshot: Snapshot, isLatest: boolean): GWSnapshot {
  const date = (snapshot.pointInTime ?? snapshot.createdTime).toISOString();
  return {
    id: snapshot.id,
    date,
    label: `${dayjs(date).format('MMM D, YYYY')}${isLatest ? ' (Latest)' : ''}`,
    sizeBytes: snapshot.fullSizeBytes ?? 0,
    userCount: 0,
    sharedDriveCount: 0,
    userDailyBudgetExceededItemsCount: snapshot.userDailyBudgetExceededItemsCount
  };
}
function saasEntitySnapshotToGWSnapshot(snapshot: SaasEntitySnapshot, isLatest: boolean): GWSnapshot {
  const date = (snapshot.dateCreated ?? new Date()).toISOString();
  return {
    id: snapshot.snapshotId ?? '',
    date,
    label: `${dayjs(date).format('MMM D, YYYY')}${isLatest ? ' (Latest)' : ''}`,
    sizeBytes: 0,
    userCount: 0,
    sharedDriveCount: 0,
    vaultId: snapshot.vaultId,
    pointInTime: snapshot.pointInTime?.toISOString()
  };
}
export const saasInventory = (httpClient: HttpClient, config: Configuration, currentProjectId: string) => {
  const getBackupSettingsApi = new GetGwsBackupFilterApi(config);
  const updateBackupSettingsApi = new UpdateShouldFullBackupResourceApi(config);
  const listOrgUnitsApi = new ListGoogleWorkspaceOrgUnitsApi(config);
  const listGroupsApi = new ListGoogleWorkspaceGroupsApi(config);
  const listSharedDrivesApi = new ListGoogleWorkspaceSharedDrivesApi(config);
  const listSnapshotsApi = new ListInventoryItemSnapshotsApi(config);
  const listM365UsersApi = new ListMicrosoft365UsersApi(config);
  const listM365GroupsApi = new ListMicrosoft365GroupsApi(config);
  const listM365GroupMembersApi = new ListMicrosoft365GroupMembersApi(config);
  const listM365SharePointSitesApi = new ListMicrosoft365SharePointSitesApi(config);
  const m365BackupScopeApi = new GetMicrosoft365BackupScopeApi(config);
  const saasEntitiesApi = new SaasEntitiesApi(config);
  const getSaasEntityIdByExternalIdApi = new GetSaasEntityIdByExternalIdApi(config);
  const listPathApi = new FileExplorerListPathApi(config);
  const searchDirectoryApi = new SearchDirectoryEntitiesApi(config);
  const listGroupConversationsApi = new ListGroupConversationsApi(config);

  // Every workspace kind lists snapshots through the same inventory endpoint and
  // differs only in its SWR cache namespace, so each passes its own baseUrl.
  const useSnapshotsRequest = (baseUrl: string, resourceId: string | null) => httpClient.execute(listSnapshotsApi.listInventoryItemSnapshots.bind(listSnapshotsApi), resourceId ? baseUrl : null, 'snapshots', currentProjectId, resourceId ?? '', 0, 100, 'eon');

  // Snapshots that captured one entity under a resource, newest first.
  // Like every DAL hook this only calls httpClient.execute*, never another use* hook: the React
  // Compiler would otherwise treat it as a hook and memoize the execute* calls (they are not
  // use-named), skipping the useSWR inside on re-renders and breaking the hook order.
  const useEntitySnapshotsRequest = ({
    resourceId,
    entityType,
    externalId,
    entityId: knownEntityId,
    enabled = true
  }: EntitySnapshotsArgs) => {
    const resolveEntityId = enabled && !knownEntityId;
    const resolved = httpClient.executeWithConfig(getSaasEntityIdByExternalIdApi.getSaasEntityIdByExternalId.bind(getSaasEntityIdByExternalIdApi), resolveEntityId && resourceId && externalId && entityType !== undefined ? entityIdByExternalIdUrl : null, `${resourceId}|${entityType}|${externalId}`, {
      keepPreviousData: false
    }, currentProjectId, resourceId,
    // Ignored when disabled: the SWR key above is null, so no request is made.
    {
      entityType: entityType ?? SaasEntityType.SAAS_ENTITY_TYPE_USER,
      externalId: externalId ?? ''
    });
    const entityId = knownEntityId ?? resolved.data?.entityId;
    const result = httpClient.executeWithConfig(saasEntitiesApi.listSaasEntitySnapshots.bind(saasEntitiesApi), enabled && resourceId && entityId ? entitySnapshotsUrl : null, `${resourceId}|${entityId}`,
    // The default SWR config keeps the previous key's data while a new key loads. Each
    // entity has its own key here, so that would surface one entity's snapshots while the
    // UI has already switched to browsing or restoring another.
    {
      keepPreviousData: false
    }, currentProjectId, resourceId, entityId ?? '', undefined, 3000);
    return {
      data: result.data?.snapshots,
      isLoading: resolved.isLoading || result.isLoading,
      error: resolved.error ?? result.error
    };
  };

  // One snapshot of an entity with its workloads' containers, which the listing leaves out. Resolves the
  // entity id under the same key as the listing, so the lookup is shared rather than repeated.
  const useEntitySnapshotDetailRequest = ({
    resourceId,
    entityType,
    externalId,
    snapshotId,
    enabled = true
  }: {
    resourceId: string;
    entityType?: SaasEntityType;
    externalId?: string;
    snapshotId?: string;
    enabled?: boolean;
  }) => {
    const resolved = httpClient.executeWithConfig(getSaasEntityIdByExternalIdApi.getSaasEntityIdByExternalId.bind(getSaasEntityIdByExternalIdApi), enabled && resourceId && externalId && entityType !== undefined ? entityIdByExternalIdUrl : null, `${resourceId}|${entityType}|${externalId}`, {
      keepPreviousData: false
    }, currentProjectId, resourceId, {
      entityType: entityType ?? SaasEntityType.SAAS_ENTITY_TYPE_USER,
      externalId: externalId ?? ''
    });
    const entityId = resolved.data?.entityId;
    const result = httpClient.executeWithConfig((...args: Parameters<typeof saasEntitiesApi.getSaasEntitySnapshot>) => saasEntitiesApi.getSaasEntitySnapshot(...args).catch(error => {
      // Gone for good, e.g. the snapshot expired after the listing was read, so it is not retried.
      if (error.code === 404) {
        return undefined;
      }
      throw error;
    }), enabled && resourceId && entityId && snapshotId ? entitySnapshotDetailUrl : null, `${resourceId}|${entityId}|${snapshotId}`, {
      keepPreviousData: false
    }, currentProjectId, resourceId, entityId ?? '', snapshotId ?? '');
    return {
      data: result.data,
      isLoading: resolved.isLoading || result.isLoading,
      error: resolved.error ?? result.error
    };
  };

  // Reads the per-snapshot directory_entities index, so it reacts to the selected
  // snapshot and is empty for pre-index snapshots — unlike the live /groups/list
  // discovery behind useGroups. `emptyOnMissingIndex` turns the 404 a snapshot
  // without that index returns into an empty list instead of an error.
  const useDirectorySearchRequest = (baseUrl: string, {
    resourceId,
    snapshotId,
    query,
    entityType,
    entityId,
    tenantOwnedOnly,
    fromTime,
    toTime,
    pageIndex,
    pageSize
  }: DirectorySearchArgs, {
    emptyOnMissingIndex = false
  }: {
    emptyOnMissingIndex?: boolean;
  } = {}) => httpClient.execute((...args: Parameters<typeof searchDirectoryApi.searchDirectoryEntities>) => searchDirectoryApi.searchDirectoryEntities(...args).catch(error => {
    if (emptyOnMissingIndex && error.code === 404) {
      const noIndex: SearchDirectoryEntitiesResponse = {
        data: [],
        pagination: {
          recordsCount: 0
        }
      };
      return noIndex;
    }
    throw error;
  }), resourceId && snapshotId ? baseUrl : null, 'directorySearch', currentProjectId, resourceId, {
    snapshotId: snapshotId ?? '',
    query,
    entityType,
    entityId,
    tenantOwnedOnly,
    fromTime,
    toTime
  }, pageIndex, pageSize);
  return {
    getEntityIdByExternalId: (resourceId: string, request: GetSaasEntityIdByExternalIdRequest) => getSaasEntityIdByExternalIdApi.getSaasEntityIdByExternalId(currentProjectId, resourceId, request),
    useEntitySnapshots: useEntitySnapshotsRequest,
    useEntitySnapshotDetail: useEntitySnapshotDetailRequest,
    googleWorkspace: {
      useSnapshots: (resourceId: string | null) => {
        const result = useSnapshotsRequest(snapshotsBaseUrl, resourceId);
        const snapshots: GWSnapshot[] = (result.data?.data ?? []).map((s: Snapshot, i: number) => snapshotToGWSnapshot(s, i === 0));
        return {
          data: snapshots,
          isLoading: result.isLoading
        };
      },
      useEntitySnapshots: (args: EntitySnapshotsArgs) => {
        const result = useEntitySnapshotsRequest(args);
        const snapshots: GWSnapshot[] = (result.data ?? []).map((s: SaasEntitySnapshot, i: number) => saasEntitySnapshotToGWSnapshot(s, i === 0));
        return {
          data: snapshots,
          // The listing as the server sent it, whose coverage verdicts the pickers judge.
          entitySnapshots: result.data ?? [],
          isLoading: result.isLoading,
          error: result.error
        };
      },
      useListPath: (resourceId: string, prefix: string, pageSize: number, snapshotId?: string, pageToken?: string, queryExecutionId?: string, sortField?: string, sortDir?: string, includeTotalCount?: boolean, enabled = true) => httpClient.execute(listPathApi.fileExplorerListPath.bind(listPathApi), enabled && resourceId ? snapshotsBaseUrl : null, 'listPath', currentProjectId, resourceId, prefix, pageSize, pageToken, queryExecutionId, undefined, snapshotId, sortField, sortDir, includeTotalCount),
      // Snapshot-independent: the entities registry is the live directory, so no snapshotId.
      useEntityUsers: (resourceId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => {
        const entities = httpClient.execute(saasEntitiesApi.listSaasEntities.bind(saasEntitiesApi), enabled && resourceId ? gwsEntitiesUrl : null, `${resourceId}|gws-users`, currentProjectId, resourceId, toEntitiesPageToken(pageToken), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_USER, search));
        return {
          ...entities,
          data: entities.data ? {
            items: (entities.data.entities ?? []).map(mapSaasEntityToGwsUser),
            totalCount: entities.data.totalCount,
            nextPageToken: fromEntitiesPageToken(entities.data.nextToken)
          } : undefined
        };
      },
      useEntitySharedDrives: (resourceId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => {
        const entities = httpClient.execute(saasEntitiesApi.listSaasEntities.bind(saasEntitiesApi), enabled && resourceId ? gwsEntitiesUrl : null, `${resourceId}|gws-shared-drives`, currentProjectId, resourceId, toEntitiesPageToken(pageToken), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_SHARED_DRIVE, search));
        return {
          ...entities,
          data: entities.data ? {
            items: (entities.data.entities ?? []).map(mapSaasEntityToGwsSharedDrive),
            totalCount: entities.data.totalCount,
            nextPageToken: fromEntitiesPageToken(entities.data.nextToken)
          } : undefined
        };
      },
      useEntityGroups: (resourceId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => {
        const entities = httpClient.execute(saasEntitiesApi.listSaasEntities.bind(saasEntitiesApi), enabled && resourceId ? gwsEntitiesUrl : null, `${resourceId}|gws-groups`, currentProjectId, resourceId, toEntitiesPageToken(pageToken), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_GROUP, search));
        return {
          ...entities,
          data: entities.data ? {
            items: (entities.data.entities ?? []).map(mapSaasEntityToGwsGroup),
            totalCount: entities.data.totalCount,
            nextPageToken: fromEntitiesPageToken(entities.data.nextToken)
          } : undefined
        };
      },
      useDirectorySearch: (args: DirectorySearchArgs) => useDirectorySearchRequest(snapshotsBaseUrl, args, {
        emptyOnMissingIndex: true
      }),
      // Lists the emails addressed to a group (distribution list), deduplicated to one row per
      // message across every member mailbox. Omitting snapshotId spans every snapshot.
      useGroupConversations: ({
        resourceId,
        groupEmail,
        snapshotId,
        pageIndex,
        pageSize,
        sortField,
        sortAsc
      }: {
        resourceId: string;
        groupEmail: string;
        snapshotId?: string;
        pageIndex: number;
        pageSize: number;
        sortField?: string;
        sortAsc?: boolean;
      }) => httpClient.execute(listGroupConversationsApi.listGroupConversations.bind(listGroupConversationsApi), resourceId && groupEmail ? snapshotsBaseUrl : null, 'groupConversations', currentProjectId, resourceId, {
        groupEmail,
        snapshotId,
        sortField,
        sortAsc
      }, pageIndex, pageSize),
      // Search and pagination run server-side; the pageToken is the page index in
      // decimal. Omitting pageSize keeps the full-listing behavior.
      useOrgUnits: (connectorId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => httpClient.execute(listOrgUnitsApi.listGoogleWorkspaceOrgUnits.bind(listOrgUnitsApi), enabled ? orgUnitsUrl : null, connectorId, currentProjectId, connectorId, pageToken, pageSize, search ? {
        search
      } : undefined),
      // Resolves specific ids regardless of pagination. Part of the SWR key, so pass them sorted.
      // Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useOrgUnitsByIds: (connectorId: string, ids: string[]) => httpClient.execute(async (projectId: string, cId: string, idsArg: string[]) => {
        const responses = await Promise.all(chunkIds(idsArg).map(idsChunk => listOrgUnitsApi.listGoogleWorkspaceOrgUnits(projectId, cId, undefined, undefined, {
          ids: idsChunk
        })));
        return {
          orgUnits: responses.flatMap(r => r.orgUnits ?? [])
        };
      }, ids.length > 0 ? orgUnitsUrl : null, connectorId, currentProjectId, connectorId, ids),
      // The org unit scope is part of the request (and so of the SWR key): the backend lists only
      // the drives a backup under that scope would take.
      useSharedDrives: (connectorId: string, orgUnitScope: {
        includedOrgUnitIds?: string[];
        excludedOrgUnitIds?: string[];
      }, search?: string, pageSize?: number, pageToken?: string, enabled = true) => httpClient.execute(listSharedDrivesApi.listGoogleWorkspaceSharedDrives.bind(listSharedDrivesApi), enabled ? sharedDrivesUrl : null, connectorId, currentProjectId, connectorId, pageToken, pageSize, {
        search: search || undefined,
        includedOrgUnitIds: orgUnitScope.includedOrgUnitIds,
        excludedOrgUnitIds: orgUnitScope.excludedOrgUnitIds
      }),
      useGroups: (connectorId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => httpClient.execute(listGroupsApi.listGoogleWorkspaceGroups.bind(listGroupsApi), enabled ? groupsUrl : null, connectorId, currentProjectId, connectorId, pageToken, pageSize, search ? {
        search
      } : undefined),
      // Resolves specific ids regardless of pagination. Part of the SWR key, so pass them sorted.
      // Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useGroupsByIds: (connectorId: string, ids: string[]) => httpClient.execute(async (projectId: string, cId: string, idsArg: string[]) => {
        const responses = await Promise.all(chunkIds(idsArg).map(idsChunk => listGroupsApi.listGoogleWorkspaceGroups(projectId, cId, undefined, undefined, {
          ids: idsChunk
        })));
        return {
          groups: responses.flatMap(r => r.groups ?? [])
        };
      }, ids.length > 0 ? groupsUrl : null, connectorId, currentProjectId, connectorId, ids),
      useBackupSettings: (resourceId: string) => httpClient.execute(getBackupSettingsApi.getGwsBackupFilter.bind(getBackupSettingsApi), backupSettingsUrl, resourceId, currentProjectId, resourceId),
      updateBackupSettings: (resourceId: string, gwsFilterJson: string) => updateBackupSettingsApi.updateShouldFullBackupResource(currentProjectId, resourceId, {
        gwsFilterJson
      }).then(() => mutate([backupSettingsUrl, resourceId, currentProjectId, resourceId]))
    },
    entraId: {
      useSnapshots: (resourceId: string | null) => useSnapshotsRequest(entraIdBaseUrl, resourceId),
      useDirectorySearch: (args: DirectorySearchArgs) => useDirectorySearchRequest(entraIdBaseUrl, args),
      // Cross-snapshot search over every Entra ID tenant: one record per directory
      // object id with the list of snapshots holding it. No generated SDK yet, so
      // the URL is hand-built; only the mock routes serve it today.
      useDirectoryGlobalSearch: ({
        query,
        entityType,
        pageIndex,
        pageSize
      }: {
        query: string;
        entityType?: DirectoryEntityType;
        pageIndex: number;
        pageSize: number;
      }) => {
        const params = new URLSearchParams({
          query,
          pageIndex: String(pageIndex),
          pageSize: String(pageSize)
        });
        if (entityType) {
          params.set('entityType', entityType);
        }
        const result = httpClient.get(query.trim() ? `/projects/${currentProjectId}/explorer/global/search/directory/entra-id?${params.toString()}` : null);
        return {
          data: result.body as EntraIdGlobalSearchResponse | undefined,
          isLoading: result.isLoading,
          error: result.error
        };
      },
      useDirectoryEdges: ({
        resourceId,
        snapshotId,
        entityId,
        entityType
      }: {
        resourceId: string;
        snapshotId?: string;
        entityId?: string;
        /** An id is only unique within a type; without it a collision can return another entity's edges. */
        entityType?: DirectoryEntityType;
      }) => httpClient.execute(searchDirectoryApi.listDirectoryEdges.bind(searchDirectoryApi), resourceId && snapshotId && entityId ? entraIdBaseUrl : null, 'directoryEdges', currentProjectId, resourceId, {
        snapshotId: snapshotId ?? '',
        entityId: entityId ?? '',
        entityType
      }),
      useDirectoryEntity: ({
        resourceId,
        snapshotId,
        entityId,
        entityType
      }: {
        resourceId: string;
        snapshotId?: string;
        entityId?: string;
        entityType?: DirectoryEntityType;
      }) => httpClient.execute(searchDirectoryApi.getDirectoryEntity.bind(searchDirectoryApi), resourceId && snapshotId && entityId && entityType ? entraIdBaseUrl : null, 'directoryEntity', currentProjectId, resourceId, {
        snapshotId: snapshotId ?? '',
        entityId: entityId ?? '',
        entityType: entityType ?? DirectoryEntityType.DIRECTORY_ENTITY_TYPE_UNSPECIFIED
      }),
      // Every edge touching any of the given entities, flattened. An empty list
      // skips the request, so a caller can chain stages off the previous one.
      useDirectoryEdgesBatch: ({
        resourceId,
        snapshotId,
        refs,
        readCache
      }: {
        resourceId: string;
        snapshotId: string;
        refs: DirectoryEntityRef[];
        readCache?: DirectoryReadCache;
      }) => httpClient.execute(async (projectId: string, batchResourceId: string, batchSnapshotId: string, batchRefs: DirectoryEntityRef[]) => {
        const responses = await mapWithConcurrency(batchRefs, DIRECTORY_BATCH_CONCURRENCY, ref => cachedDirectoryRead(readCache, `edges|${projectId}|${batchResourceId}|${batchSnapshotId}|${ref.entityType}|${ref.entityId}`, () => searchDirectoryApi.listDirectoryEdges(projectId, batchResourceId, {
          snapshotId: batchSnapshotId,
          ...ref
        })));
        return responses.flatMap(response => response.data ?? []);
      }, resourceId && snapshotId && refs.length > 0 ? entraIdBaseUrl : null, 'directoryEdgesBatch', currentProjectId, resourceId, snapshotId, refs),
      // The entity records that exist in the snapshot. With allowMissing, an
      // entity the snapshot's index does not hold (404) is left out; without it
      // the batch fails, for a caller whose answer would be wrong without it.
      useDirectoryEntityBatch: ({
        resourceId,
        snapshotId,
        refs,
        readCache,
        allowMissing = true
      }: {
        resourceId: string;
        snapshotId: string;
        refs: DirectoryEntityRef[];
        readCache?: DirectoryReadCache;
        allowMissing?: boolean;
      }) => httpClient.execute(async (projectId: string, batchResourceId: string, batchSnapshotId: string, batchRefs: DirectoryEntityRef[], batchAllowMissing: boolean) => {
        const responses = await mapWithConcurrency(batchRefs, DIRECTORY_BATCH_CONCURRENCY, ref => cachedDirectoryRead(readCache, `entity|${projectId}|${batchResourceId}|${batchSnapshotId}|${ref.entityType}|${ref.entityId}|${batchAllowMissing}`, () => searchDirectoryApi.getDirectoryEntity(projectId, batchResourceId, {
          snapshotId: batchSnapshotId,
          ...ref
        }).catch(error => {
          if (batchAllowMissing && error.code === 404) {
            return undefined;
          }
          throw error;
        })));
        return responses.filter(response => response !== undefined);
      }, resourceId && snapshotId && refs.length > 0 ? entraIdBaseUrl : null, 'directoryEntityBatch', currentProjectId, resourceId, snapshotId, refs, allowMissing)
    },
    microsoft365: {
      // Search and pagination run server-side (the gateway filters in the DB and
      // returns nextPageToken); every distinct (search, page) tuple is its own
      // SWR key. Omitting pageSize keeps the full-listing behavior. enabled=false
      // skips the request entirely (null SWR key) so callers can mount the hook
      // unconditionally and only fetch when their UI needs the data.
      // One user by Graph id, for a deep link that knows only the mailbox owner's id.
      useUserByExternalId: (connectorId: string, externalId: string | undefined, enabled = true) => {
        const entities = httpClient.execute(saasEntitiesApi.listSaasEntities.bind(saasEntitiesApi), enabled && externalId ? m365EntitiesUrl : null, `${connectorId}|user|${externalId ?? ''}`, currentProjectId, connectorId, undefined, 1, {
          filters: {
            type: {
              _in: [SaasEntityType.SAAS_ENTITY_TYPE_USER]
            },
            externalId: {
              _in: [externalId ?? '']
            }
          }
        });
        const entity = entities.data?.entities?.[0];
        return {
          ...entities,
          data: entity ? mapSaasEntityToMicrosoft365User(entity) : undefined
        };
      },
      useUsers: (connectorId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true, microsoft365?: Microsoft365EntityFilters) => {
        const entities = httpClient.execute(saasEntitiesApi.listSaasEntities.bind(saasEntitiesApi), enabled ? m365EntitiesUrl : null, `${connectorId}|users`, currentProjectId, connectorId, toEntitiesPageToken(pageToken), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_USER, search, microsoft365));
        return {
          ...entities,
          data: entities.data ? {
            users: (entities.data.entities ?? []).map(mapSaasEntityToMicrosoft365User),
            totalCount: entities.data.totalCount,
            nextPageToken: fromEntitiesPageToken(entities.data.nextToken)
          } : undefined
        };
      },
      useGroups: (connectorId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true) => httpClient.execute(listM365GroupsApi.listMicrosoft365Groups.bind(listM365GroupsApi), enabled ? m365GroupsUrl : null, connectorId, currentProjectId, connectorId, pageToken, pageSize, search ? {
        search
      } : undefined),
      // Resolves specific ids regardless of pagination. Part of the SWR key, so pass them sorted.
      // Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useGroupsByIds: (connectorId: string, ids: string[]) => httpClient.execute(async (projectId: string, cId: string, idsArg: string[]) => {
        const responses = await Promise.all(chunkIds(idsArg).map(idsChunk => listM365GroupsApi.listMicrosoft365Groups(projectId, cId, undefined, undefined, {
          ids: idsChunk
        })));
        return {
          groups: responses.flatMap(r => r.groups ?? [])
        };
      }, ids.length > 0 ? m365GroupsUrl : null, connectorId, currentProjectId, connectorId, ids),
      // Resolves specific ids regardless of pagination, chunked past the gateway's per-request id cap.
      // Part of the SWR key, so pass them sorted.
      useUsersByIds: (connectorId: string, ids: string[]) => httpClient.execute(async (projectId: string, cId: string, idsArg: string[]) => {
        const responses = await Promise.all(chunkIds(idsArg).map(idsChunk => listM365UsersApi.listMicrosoft365Users(projectId, cId, undefined, undefined, {
          ids: idsChunk
        })));
        return {
          users: responses.flatMap(r => r.users ?? [])
        };
      }, ids.length > 0 ? m365UsersUrl : null, connectorId, currentProjectId, connectorId, ids),
      // Search, sort and pagination all run server-side, same as useUsers; the
      // pageToken is the page index in decimal. Omitting pageSize keeps the
      // full-listing behavior.
      useGroupMembers: (connectorId: string, groupId: string | null, search?: string, pageSize?: number, pageToken?: string, sort?: ListMicrosoft365GroupMembersSort) => httpClient.execute(listM365GroupMembersApi.listMicrosoft365GroupMembers.bind(listM365GroupMembersApi), connectorId && groupId ? m365GroupMembersUrl : null, groupId ?? '', currentProjectId, connectorId, groupId ?? '', pageToken, pageSize, search || sort ? {
        search,
        sort
      } : undefined),
      // Which users, sites and Teams a backup policy currently covers. Pass a draft
      // config to preview an unsaved selection instead of the tenant's policies.
      useBackupScope: (connectorId: string, config?: Microsoft365PolicyConfig, enabled = true) => httpClient.execute(m365BackupScopeApi.getMicrosoft365BackupScope.bind(m365BackupScopeApi), enabled && connectorId ? m365BackupScopeUrl : null, `${connectorId}|${config ? JSON.stringify(config) : 'saved'}`, currentProjectId, connectorId, config ? {
        config
      } : undefined),
      useSharePointSites: (connectorId: string, search?: string, pageSize?: number, pageToken?: string, enabled = true, microsoft365?: Microsoft365EntityFilters) => {
        const entities = httpClient.execute(saasEntitiesApi.listSaasEntities.bind(saasEntitiesApi), enabled ? m365EntitiesUrl : null, `${connectorId}|sharepoint-sites`, currentProjectId, connectorId, toEntitiesPageToken(pageToken), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_SHAREPOINT_SITE, search, microsoft365));
        return {
          ...entities,
          data: entities.data ? {
            sharepointSites: (entities.data.entities ?? []).map(mapSaasEntityToSharePointSite),
            totalCount: entities.data.totalCount,
            nextPageToken: fromEntitiesPageToken(entities.data.nextToken)
          } : undefined
        };
      },
      // Teams live only in the entity registry, so this list pages by index like the registry does;
      // nextPageToken is the next page's index, which is what the callers hand back in.
      useTeams: (connectorId: string, search?: string, pageSize?: number, pageIndex = 0, enabled = true, microsoft365?: Microsoft365EntityFilters) => {
        const entities = httpClient.execute(saasEntitiesApi.listSaasEntities.bind(saasEntitiesApi), enabled && connectorId ? m365EntitiesUrl : null, `${connectorId}|teams`, currentProjectId, connectorId, toEntitiesPageToken(String(pageIndex)), pageSize, entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_TEAM, search, microsoft365));
        return {
          ...entities,
          data: entities.data ? {
            teams: (entities.data.entities ?? []).map(mapSaasEntityToTeam),
            totalCount: entities.data.totalCount,
            nextPageToken: entities.data.nextToken ? String(pageIndex + 1) : undefined
          } : undefined
        };
      },
      // Resolves specific Teams by id regardless of pagination. Part of the SWR key, so pass them
      // sorted. Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useTeamsByIds: (connectorId: string, ids: string[]) => httpClient.execute(async (projectId: string, cId: string, idsArg: string[]) => {
        const responses = await Promise.all(chunkIds(idsArg).map(idsChunk => saasEntitiesApi.listSaasEntities(projectId, cId, undefined, idsChunk.length, {
          filters: {
            type: {
              _in: [SaasEntityType.SAAS_ENTITY_TYPE_TEAM]
            },
            externalId: {
              _in: idsChunk
            }
          }
        })));
        return {
          teams: responses.flatMap(r => (r.entities ?? []).map(mapSaasEntityToTeam))
        };
      }, ids.length > 0 ? m365EntitiesUrl : null, `${connectorId}|teams-by-id`, currentProjectId, connectorId, ids),
      // Resolves specific ids regardless of pagination. Part of the SWR key, so pass them sorted.
      // Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useSharePointSitesByIds: (connectorId: string, ids: string[]) => httpClient.execute(async (projectId: string, cId: string, idsArg: string[]) => {
        const responses = await Promise.all(chunkIds(idsArg).map(idsChunk => listM365SharePointSitesApi.listMicrosoft365SharePointSites(projectId, cId, undefined, undefined, {
          ids: idsChunk
        })));
        return {
          sharepointSites: responses.flatMap(r => r.sharepointSites ?? [])
        };
      }, ids.length > 0 ? m365SharePointSitesUrl : null, connectorId, currentProjectId, connectorId, ids)
    }
  };
};